import PropTypes from "prop-types";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import ProductCard from "./ProductCard";
import s from "./YouMayAlsoLike.module.css";

const MAX_RECOMMENDATIONS = 4;

function getVariantIds(product) {
  const ids = new Set();
  ids.add(product.product_id);
  if (Array.isArray(product._variants)) {
    for (const v of product._variants) {
      ids.add(v.product_id);
    }
  }
  return ids;
}

function relevanceScore(candidate, current) {
  let score = 0;

  // Same character is the strongest signal
  if (candidate._character && candidate._character === current._character) {
    score += 4;
  }

  // Same format category (8x10, poster, apparel, etc.)
  if (candidate._formatCategory === current._formatCategory) {
    score += 2;
  }

  // Shares a signing type with the current product
  const currentTypes = current._variants
    ? new Set(current._variants.map((v) => v.signed_type))
    : new Set([current.signed_type]);
  const candidateTypes = candidate._variants
    ? new Set(candidate._variants.map((v) => v.signed_type))
    : new Set([candidate.signed_type]);
  if (Array.from(candidateTypes).some((t) => currentTypes.has(t))) {
    score += 1;
  }

  // Price proximity (within 30% = +1 point)
  const currentPrice = current._totalPrice || 0;
  const candidatePrice = candidate._totalPrice || 0;
  if (currentPrice > 0 && candidatePrice > 0) {
    const ratio = candidatePrice / currentPrice;
    if (ratio >= 0.7 && ratio <= 1.3) score += 1;
  }

  return score;
}

export default function YouMayAlsoLike({
  allProducts,
  currentProduct,
  route,
  talentName,
  onProductClick,
}) {
  const recommendations = useMemo(() => {
    if (!allProducts?.length || !currentProduct) return [];

    const excludeIds = getVariantIds(currentProduct);

    return allProducts
      .filter(
        (p) =>
          !excludeIds.has(p.product_id) &&
          !p.vaulted_until &&
          !(p.stock !== null && Number.isInteger(p.stock) && p.stock <= 0),
      )
      .map((p) => ({ product: p, score: relevanceScore(p, currentProduct) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_RECOMMENDATIONS)
      .map((entry) => entry.product);
  }, [allProducts, currentProduct]);

  if (recommendations.length === 0) return null;

  const heading = talentName ? `More from ${talentName}` : "You May Also Like";

  return (
    <section className={s.section} aria-labelledby="more-from-talent">
      <div className={s.header}>
        <h3 id="more-from-talent" className={s.title}>
          {heading}
        </h3>
        <Link to={`/${route}`} className={s.viewAll}>
          View All
        </Link>
      </div>
      <div className={s.grid}>
        {recommendations.map((p) => (
          <ProductCard
            key={p.product_id}
            product={p}
            onClick={onProductClick}
          />
        ))}
      </div>
    </section>
  );
}

YouMayAlsoLike.propTypes = {
  allProducts: PropTypes.array,
  currentProduct: PropTypes.object,
  route: PropTypes.string.isRequired,
  talentName: PropTypes.string,
  onProductClick: PropTypes.func.isRequired,
};
